/**
 * TemplateForm Component
 *
 * Purpose: Main wrapper component for template-driven forms. Initializes form state,
 *          provides context to child components, passes helpers to the template render
 *          function, and exposes an imperative handle for external button control.
 * Architecture: Uses forwardRef + useImperativeHandle to expose TemplateFormHandle.
 *               Delegates state management to useTemplateForm hook. Provides
 *               TemplateFormContext to all descendants. Does NOT render buttons —
 *               external buttons use the ref handle.
 * Dependencies: React (forwardRef, useImperativeHandle, useMemo), MUI (Box, CircularProgress,
 *               Typography), TemplateFormContext (TemplateFormProvider), useTemplateForm hook,
 *               TemplateField, ExtraFields, ./types (TemplateFormProps, TemplateFormHandle, TemplateHelpers)
 * Author: Development Team
 * Created: 2026-02-15
 */

import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { TemplateFormProvider } from './TemplateFormContext';
import { PlanningCurrencyProvider } from './PlanningCurrencyContext';
import { useTemplateForm } from './useTemplateForm';
import TemplateField from './TemplateField';
import ExtraFields from './ExtraFields';
import type { TemplateFormProps, TemplateFormHandle, TemplateHelpers } from './types';
import { PickerFilters, PickerScope } from '../../context/PickerScopeContext';
import { resolvePickerPermission } from '../../utils/pickerPermission';

/**
 * TemplateForm — template-driven form with metadata-connected field rendering
 *
 * Purpose: Renders a form whose layout is defined by a FormTemplate while field
 *          types, validation, and options are driven by backend metadata
 * Logic: 1) Fetches view config and builds field registry via useTemplateForm
 *        2) Provides context (registry, values, errors, actions) to descendants
 *        3) Passes TemplateHelpers (Field, ExtraFields, formState, formActions) to template.render()
 *        4) Exposes TemplateFormHandle via ref for external submit/reset/validate
 * Input: TemplateFormProps — template, tenantId, mode, initialData, onSubmit, onChange, readOnly, loading
 * Output: Rendered template form, loading spinner, or error message
 */
const TemplateForm = forwardRef<TemplateFormHandle, TemplateFormProps>(
  function TemplateForm(props, ref) {
    const {
      template,
      tenantId,
      mode,
      initialData,
      entityId,
      onSubmit,
      onChange,
      readOnly,
      loading,
      indicatorMode,
      appliedFieldKeys,
      fieldResults,
      pickerPermission,
    } = props;

    // ORCH-366: pickers are scoped by the template's model and mode unless overridden
    const pickerScope =
      pickerPermission === undefined
        ? resolvePickerPermission(template.modelId, readOnly ? 'view' : (mode ?? 'create'))
        : pickerPermission;

    const {
      contextValue,
      formState,
      formActions,
      checkSubmitReadiness,
      viewLoading,
      viewError,
      pickerLocked,
    } = useTemplateForm({
      template,
      tenantId,
      mode,
      initialData,
      entityId,
      onSubmit,
      onChange,
      readOnly,
      loading,
      indicatorMode,
      appliedFieldKeys,
      fieldResults,
      pickerPermission: pickerScope,
    });

    // Expose handle via ref for external button control
    useImperativeHandle(
      ref,
      () => ({
        submit: formActions.submit,
        reset: formActions.reset,
        validate: formActions.validate,
        isDirty: formState.isDirty,
        isSubmitting: formState.isSubmitting,
        isValid: formState.isValid,
        getData: contextValue.actions.getData,
        isReadyForSubmit: () => {
          if (viewLoading || loading || contextValue.fieldRegistry.size === 0) return false;
          return checkSubmitReadiness();
        },
        /**
         * Populate form fields from an LLM-emitted draft.
         *
         * Note on list/MDM fields: this helper sets form state directly, bypassing
         * `MetadataProcessor`. That's safe for the LLM flow because list values
         * coming from the orchestrator are already resolved to GLOBAL MDM UUIDs
         * (the tool layer in LLM-Service resolves labels → IDs before emitting).
         * The submit pipeline (`processMetadataInput` → backend MetadataProcessor)
         * is the authoritative validator on the *storage* side; this helper only
         * touches *form state*. If a future LLM caller emits raw labels instead
         * of UUIDs, we'd need to resolve them here.
         *
         * Only `fd_budget` gets explicit numeric coercion — the field validator
         * expects a number, and assistants commonly emit strings.
         */
        applyAssistantDraft: (fields: Record<string, unknown>) => {
          const merged: Record<string, unknown> = {};
          Object.entries(fields).forEach(([k, v]) => {
            if (v === undefined) return;
            if (k === 'fd_budget' && v !== null && v !== '') {
              const n = Number(v);
              merged[k] = Number.isFinite(n) ? n : v;
              return;
            }
            // Multiselect list fields must hold arrays. Mapping-confirmation
            // approvals emit a single UUID scalar, which validateField and the
            // backend MetadataProcessor both reject — coerce scalar → [scalar].
            const def = contextValue.fieldRegistry.get(k)?.field_definition;
            if (
              def?.datatype === 'list' &&
              def.is_multiselect &&
              v !== null &&
              v !== '' &&
              !Array.isArray(v)
            ) {
              merged[k] = [v];
              return;
            }
            merged[k] = v;
          });
          // setValues fires the template's onFieldChange for each field, so
          // field-dependent state (e.g. CreateMediaPlanTemplate resolving the
          // planning currency behind the Budget symbol) updates here the same
          // way it does for a manual edit (ORCH-377).
          contextValue.actions.setValues(merged as Record<string, any>);
        },
      }),
      [
        formActions,
        formState,
        contextValue.actions,
        contextValue.fieldRegistry,
        viewLoading,
        loading,
        checkSubmitReadiness,
      ]
    );

    // Build helpers passed to template.render()
    const helpers: TemplateHelpers = useMemo(
      () => ({
        Field: TemplateField,
        ExtraFields,
        formState,
        formActions,
      }),
      [formState, formActions]
    );

    // Loading state
    if (viewLoading || loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Error state
    if (viewError) {
      return (
        <Box sx={{ py: 4 }}>
          <Typography color="error">Failed to load form configuration: {viewError}</Typography>
        </Box>
      );
    }

    return (
      <PickerScope permission={pickerScope}>
        {/* ORCH-400: pickers narrow by the form's own values; locked chips keep no delete */}
        <PickerFilters
          modelId={template.modelId}
          tenantId={tenantId}
          values={contextValue.values}
          locked={pickerLocked}
        >
          <TemplateFormProvider value={contextValue}>
            <PlanningCurrencyProvider value={contextValue.planningCurrencyCode}>
              {template.render(helpers)}
            </PlanningCurrencyProvider>
          </TemplateFormProvider>
        </PickerFilters>
      </PickerScope>
    );
  }
);

export default TemplateForm;
